import { Check, X } from "lucide-react";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { TrustBar } from "@/components/TrustBar";
import { OffersGrid } from "@/components/OffersGrid";
import { WhyBroker } from "@/components/WhyBroker";
import { HowItWorks } from "@/components/HowItWorks";
import { WhatsIncluded } from "@/components/WhatsIncluded";
import { Reviews } from "@/components/Reviews";
import { ConsultantCard } from "@/components/ConsultantCard";
import { PreventivoSection } from "@/components/PreventivoSection";
import { Faq } from "@/components/Faq";
import { CtaFinal } from "@/components/CtaFinal";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useSeo, faqSchema } from "@/lib/seo";
import { FAQ_PRIVATI, waLink } from "@/data/content";

const HERO_PHOTO = "https://images.unsplash.com/photo-1690278289651-895463644114?crop=entropy&cs=srgb&fm=jpg&q=85";

const CONFRONTO = [
  { voce: "Capitale iniziale", fin: "Anticipo + maxi-canone o finanziamento", nlt: "Anche zero anticipo" },
  { voce: "Assicurazione RCA e Kasko", fin: "A parte, ogni anno", nlt: "Incluse nel canone" },
  { voce: "Manutenzione e guasti", fin: "A carico tuo, imprevedibili", nlt: "Incluse, costo certo" },
  { voce: "Cristalli ed eventi atmosferici", fin: "Polizze extra a carico tuo", nlt: "Protezione sempre inclusa" },
  { voce: "Tutela legale e infortunio conducente", fin: "Da aggiungere a parte", nlt: "Incluse nel canone" },
  { voce: "Svalutazione e rivendita", fin: "Rischio tutto tuo", nlt: "Non esiste: restituisci e basta" },
  { voce: "Maxi-rata finale", fin: "Spesso presente", nlt: "Mai" },
];

const FinanziamentoVsNlt = () => (
  <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20" data-testid="confronto-nlt">
    <Reveal>
      <p className="text-xs font-semibold uppercase tracking-widest text-xmove-orange">Il confronto onesto</p>
      <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold text-xmove-ink sm:text-4xl">
        Finanziamento o noleggio? Facciamo i conti veri.
      </h2>
      <p className="mt-3 max-w-2xl text-base text-xmove-muted md:text-lg">
        La rata del finanziamento è solo l'inizio. Ecco cosa cambia voce per voce.
      </p>
    </Reveal>
    <Reveal delay={0.1}>
      <div className="mt-10 overflow-hidden rounded-2xl border border-xmove-ink/8 bg-white shadow-soft">
        <div className="hidden grid-cols-3 gap-4 border-b border-xmove-ink/8 bg-xmove-cream px-5 py-4 text-xs font-bold uppercase tracking-wider text-xmove-muted sm:grid sm:px-6">
          <span>Voce</span><span>Acquisto / Finanziamento</span><span className="text-xmove-orange">Noleggio XMOVE</span>
        </div>
        {CONFRONTO.map((r) => (
          <div key={r.voce} className="grid grid-cols-1 gap-2.5 border-b border-xmove-ink/6 px-5 py-4 last:border-0 sm:grid-cols-3 sm:items-center sm:gap-4 sm:px-6 sm:py-5">
            <span className="text-[13px] font-bold text-xmove-ink sm:text-sm">{r.voce}</span>
            <span className="text-xs text-xmove-muted sm:text-sm">
              <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wider text-xmove-muted/70 sm:hidden">Acquisto / Finanziamento</span>
              <span className="flex items-start gap-1.5">
                <X size={15} className="mt-0.5 shrink-0 text-red-400" />{r.fin}
              </span>
            </span>
            <span className="text-xs font-semibold text-xmove-ink sm:text-sm">
              <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wider text-xmove-orange/80 sm:hidden">Noleggio XMOVE</span>
              <span className="flex items-start gap-1.5">
                <Check size={15} className="mt-0.5 shrink-0 text-xmove-green" />{r.nlt}
              </span>
            </span>
          </div>
        ))}
      </div>
    </Reveal>
  </section>
);

const PrivatiSeoContent = () => (
  <section className="mx-auto max-w-3xl px-5 py-16 lg:px-8" data-testid="privati-seo-content">
    <Reveal>
      <h2 className="font-display text-2xl font-bold text-xmove-ink sm:text-3xl">
        Noleggio lungo termine per privati: l'auto nuova senza finanziamento e senza pensieri
      </h2>
      <div className="mt-6 space-y-5 text-base leading-relaxed text-xmove-muted">
        <p>
          Fino a pochi anni fa il noleggio a lungo termine era roba da aziende. Oggi è la formula che un
          privato su dieci valuta quando cambia auto, e il motivo è semplice: guidi un'auto nuova, in
          garanzia, con ogni costo già dentro un canone mensile fisso. Niente finanziamento in banca,
          niente maxi-rata, niente rivendita dell'usato a fine vita.
        </p>
        <h3 className="font-display text-xl font-bold text-xmove-ink">Quanto costa davvero un'auto di proprietà?</h3>
        <p>
          La rata del finanziamento è solo una parte: aggiungi assicurazione (RCA e Kasko), bollo, tagliandi,
          pneumatici, guasti fuori garanzia e soprattutto la svalutazione — un'auto nuova perde oltre il 40%
          del valore nei primi tre anni. Sommando tutto, il canone del noleggio è spesso alla pari o più
          conveniente, con la differenza che è certo e scritto nero su bianco prima di firmare.
        </p>
        <h3 className="font-display text-xl font-bold text-xmove-ink">Anche a zero anticipo, anche con budget piccoli</h3>
        <p>
          Non serve capitale iniziale: molte formule XMOVE partono a zero anticipo e con canoni sotto i
          300 euro al mese trovi auto nuove, ibride e perfettamente accessoriare. Indicaci il tuo budget
          mensile nel modulo e ti proponiamo solo ciò che ci sta dentro, senza sorprese.
        </p>
        <h3 className="font-display text-xl font-bold text-xmove-ink">Chi te lo propone fa la differenza</h3>
        <p>
          Chi vende un solo marchio ti propone il suo listino. XMOVE lavora dalla tua parte: consultiamo
          lo stock più ampio d'Italia e ti portiamo il canone più basso a parità di servizi, con un
          consulente dedicato che ti segue dalla scelta alla consegna — e resta disponibile anche dopo.
        </p>
      </div>
    </Reveal>
  </section>
);

export default function Privati() {
  useSeo({
    title: "Noleggio Lungo Termine Privati: Offerte {Mese Anno} | XMOVE",
    description:
      "Noleggio lungo termine per privati: canone fisso tutto incluso, anche a zero anticipo, senza finanziamento né maxi-rata. Auto nuova dal budget che decidi tu. Preventivo gratuito.",
    path: "/noleggio-lungo-termine-privati/",
    jsonLd: [faqSchema(FAQ_PRIVATI)],
  });

  return (
    <div className="pb-[72px] md:pb-0" data-testid="privati-page">
      <Header />
      <main>
        <Hero
          eyebrow="Dedicato ai Privati · Anche zero anticipo"
          h1Lines={["Noleggio Lungo Termine", "per Privati"]}
          subtitle="L'auto nuova che vuoi a canone fisso, anche a zero anticipo: assicurazione, manutenzione, protezione cristalli ed eventi atmosferici inclusi. Niente finanziamento, niente maxi-rata, niente svalutazione. Il budget lo decidi tu."
          photo={HERO_PHOTO}
          photoAlt="Auto nuova a noleggio lungo termine per privati"
        />
        <Marquee />
        <TrustBar />
        <FinanziamentoVsNlt />
        <div className="bg-white">
          <OffersGrid
            target="privati"
            title="Offerte per privati, anche sotto i 300€"
            subtitle="Auto nuove con canone tutto incluso e anticipo flessibile: indica il tuo budget e facciamo il resto."
          />
        </div>
        <WhyBroker />
        <HowItWorks />
        <WhatsIncluded dark />
        <PrivatiSeoContent />
        <Reviews limit={3} />
        <ConsultantCard />
        <PreventivoSection
          idPrefix="privati"
          title="Il tuo preventivo, dal budget che decidi tu"
          subtitle="Indica quanto vuoi spendere al mese: ti richiamiamo in pochi minuti con le migliori proposte reali."
        />
        <Faq faqs={FAQ_PRIVATI} title="Domande frequenti — Privati" />
        <CtaFinal
          title="Dicci il tuo budget. All'auto pensiamo noi."
          subtitle="Compila il modulo con quanto vuoi spendere al mese: ti richiamiamo in pochi minuti con le migliori offerte reali, senza impegno."
        />
      </main>
      <Footer />
      <MobileStickyBar waText={waLink("Salve XMOVE, sono un privato e vorrei un preventivo per il noleggio lungo termine.")} />
    </div>
  );
}
